import { useEffect, useRef, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { 
  TrendingUp, 
  Globe, 
  Shield, 
  Target, 
  Award, 
  ChevronDown,
  CheckCircle,
  ArrowUpRight,
  ArrowRight,
  Gem,
  Building2,
  Landmark,
  LineChart,
  Sparkles
} from "lucide-react";
import { SocialShare } from "@/components/SocialShare";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from "recharts";

const pSuiteData = [
  { name: "P1", return: 9.63, benchmark: 7.56, excess: 2.07 },
  { name: "P2", return: 11.64, benchmark: 9.34, excess: 2.30 },
  { name: "P3", return: 13.40, benchmark: 11.02, excess: 2.38 },
  { name: "P4", return: 15.54, benchmark: 12.77, excess: 2.77 },
  { name: "P5", return: 17.01, benchmark: 14.24, excess: 2.77 },
];

const rSuiteData = [
  { name: "R1", return: 7.57, benchmark: 7.56, excess: 0.01 },
  { name: "R2", return: 10.06, benchmark: 9.34, excess: 0.72 },
  { name: "R3", return: 11.65, benchmark: 11.02, excess: 0.63 },
  { name: "R4", return: 13.63, benchmark: 12.77, excess: 0.86 },
  { name: "R5", return: 15.22, benchmark: 14.24, excess: 0.98 },
];

const topPerformers = [
  { fund: "Ninety One Global Gold", return: "+167.35%", category: "Gold", gradient: "from-amber-400 to-yellow-500" },
  { fund: "Artemis SmartGARP European Equity", return: "+54.70%", category: "Europe", gradient: "from-violet-500 to-purple-600" },
  { fund: "Artemis SmartGARP Global EM", return: "+28.16%", category: "Emerging Markets", gradient: "from-emerald-500 to-teal-600" },
  { fund: "HSBC European Index", return: "+25.58%", category: "Europe", gradient: "from-blue-500 to-indigo-600" },
  { fund: "Lazard Infrastructure", return: "+24.94%", category: "Infrastructure", gradient: "from-orange-400 to-red-500" },
  { fund: "HSBC FTSE 100 Index", return: "+24.54%", category: "UK", gradient: "from-cyan-400 to-blue-500" },
];

const marketHighlights = [
  { region: "Emerging Markets", return: "~22%", icon: Globe, gradient: "from-emerald-500 to-teal-600" },
  { region: "Gold", return: "~60%", icon: Gem, gradient: "from-amber-400 to-yellow-500" },
  { region: "UK Equities", return: "~25%", icon: Landmark, gradient: "from-blue-500 to-indigo-600" },
  { region: "Europe", return: "~25%", icon: Building2, gradient: "from-violet-500 to-purple-600" },
];

const commitments = [
  { title: "True Diversification", desc: "Exposure across geographies, sectors, and asset classes" },
  { title: "Quality Focus", desc: "Emphasis on sustainable businesses with competitive advantages" },
  { title: "Pragmatic Approach", desc: "Combining low-cost indexing with skilled active management" },
  { title: "Risk Management", desc: "Each portfolio calibrated to its stated risk objective" },
  { title: "Cost Efficiency", desc: "Maximising the proportion of returns reaching clients" },
  { title: "Long-Term Perspective", desc: "Building wealth over full market cycles" },
];

// Animated section component
const AnimatedSection = ({ 
  children, 
  className = "", 
  delay = 0,
  direction = "up" 
}: { 
  children: React.ReactNode; 
  className?: string; 
  delay?: number;
  direction?: "up" | "left" | "right" | "scale";
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(element);
        }
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );

    observer.observe(element);
    return () => observer.unobserve(element);
  }, []);

  const getTransform = () => {
    if (isVisible) return "translate(0, 0) scale(1)";
    switch (direction) {
      case "up": return "translateY(60px)";
      case "left": return "translateX(60px)";
      case "right": return "translateX(-60px)";
      case "scale": return "scale(0.9)";
      default: return "translateY(60px)";
    }
  };

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: getTransform(),
        transition: `all 0.9s cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
};

// Counter animation component
const AnimatedCounter = ({ 
  value, 
  suffix = "", 
  prefix = "",
  duration = 2000 
}: { 
  value: number; 
  suffix?: string; 
  prefix?: string;
  duration?: number;
}) => {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          let start = 0;
          const increment = value / (duration / 16);
          const timer = setInterval(() => {
            start += increment;
            if (start >= value) {
              setCount(value);
              clearInterval(timer);
            } else {
              setCount(start);
            }
          }, 16);
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(element);
    return () => observer.unobserve(element);
  }, [value, duration, hasAnimated]);

  return (
    <span ref={ref}>
      {prefix}{count.toFixed(2)}{suffix}
    </span>
  );
};

// Social Share Section Component
const ShareSection = () => {
  return (
    <div className="flex flex-col items-center gap-3">
      <SocialShare 
        title="CBAM End of Year Investment Review 2025" 
        variant="light"
      />
    </div>
  );
};

const YearEndReview2025 = () => {
  const [heroLoaded, setHeroLoaded] = useState(false);

  useEffect(() => {
    setTimeout(() => setHeroLoaded(true), 100);
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section - Full viewport with animated gradient */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Animated background */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary/90 to-primary/80" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-white/10 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-black/20 via-transparent to-transparent" />
        
        {/* Animated floating elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-white/5 rounded-full blur-3xl animate-pulse" style={{ animationDuration: "4s" }} />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-white/5 rounded-full blur-3xl animate-pulse" style={{ animationDuration: "6s", animationDelay: "1s" }} />
          <div className="absolute top-1/2 right-1/3 w-48 h-48 bg-white/5 rounded-full blur-2xl animate-pulse" style={{ animationDuration: "5s", animationDelay: "2s" }} />
        </div>
        
        {/* Geometric pattern overlay */}
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />

        <div className="container mx-auto px-4 relative z-10">
          <div className={`max-w-5xl mx-auto text-center transition-all duration-1000 ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
            {/* Sparkle badge */}
            <div className={`inline-flex items-center gap-2 mb-8 px-4 py-2 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 transition-all duration-1000 delay-200 ${heroLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-90'}`}>
              <Sparkles className="w-4 h-4 text-white" />
              <span className="text-white/90 text-sm font-medium">Performance Review</span>
            </div>
            
            <h1 className={`text-5xl md:text-7xl lg:text-8xl font-bold mb-6 text-white leading-tight transition-all duration-1000 delay-300 ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              End of Year
              <br />
              <span className="bg-gradient-to-r from-white via-white/90 to-white/70 bg-clip-text text-transparent">
                Investment Review
              </span>
            </h1>
            
            <p className={`text-xl md:text-2xl lg:text-3xl text-white/80 mb-12 font-light transition-all duration-1000 delay-500 ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              Exceptional Returns Across All Portfolios
            </p>
            
            {/* Stats cards */}
            <div className={`flex flex-wrap justify-center gap-4 md:gap-6 transition-all duration-1000 delay-700 ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <div className="group bg-white/10 backdrop-blur-md rounded-2xl px-6 md:px-8 py-5 border border-white/20 hover:bg-white/20 hover:scale-105 transition-all duration-300 cursor-default">
                <div className="text-3xl md:text-5xl font-bold text-white mb-1">+17.01%</div>
                <div className="text-sm text-white/70">Top P Suite Return</div>
              </div>
              <div className="group bg-white/10 backdrop-blur-md rounded-2xl px-6 md:px-8 py-5 border border-white/20 hover:bg-white/20 hover:scale-105 transition-all duration-300 cursor-default">
                <div className="text-3xl md:text-5xl font-bold text-white mb-1">+5.36%</div>
                <div className="text-sm text-white/70">vs ARC MPS Benchmark</div>
              </div>
              <div className="group bg-white/10 backdrop-blur-md rounded-2xl px-6 md:px-8 py-5 border border-white/20 hover:bg-white/20 hover:scale-105 transition-all duration-300 cursor-default">
                <div className="text-3xl md:text-5xl font-bold text-white mb-1">100%</div>
                <div className="text-sm text-white/70">Benchmark Beat Rate</div>
              </div>
            </div>

            {/* Social Share */}
            <div className={`mt-10 transition-all duration-1000 delay-900 ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <ShareSection />
            </div>
          </div>
        </div>
        
        {/* Scroll indicator */}
        <div className={`absolute bottom-8 left-1/2 -translate-x-1/2 transition-all duration-1000 delay-1000 ${heroLoaded ? 'opacity-100' : 'opacity-0'}`}>
          <div className="flex flex-col items-center gap-2 text-white/60">
            <span className="text-xs uppercase tracking-widest">Scroll to explore</span>
            <ChevronDown className="w-5 h-5 animate-bounce" />
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 md:py-32 bg-background">
        <div className="container mx-auto px-4">
          <AnimatedSection className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-5xl font-bold mb-8">Dear Valued Client</h2>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-6">
              As 2025 draws to a close, we are delighted to report that it has been an exceptional year for financial markets and, more importantly, for your Cape Berkshire portfolios. All of our portfolio suites delivered strong returns whilst exceeding their respective benchmarks.
            </p>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
              The breadth of 2025's rally stands as powerful validation of our core investment philosophy:{" "}
              <span className="text-primary font-semibold">true diversification provides superior risk-adjusted returns over time</span>.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Market Performance Cards */}
      <section className="py-20 md:py-32 bg-muted/30 overflow-hidden">
        <div className="container mx-auto px-4">
          <AnimatedSection className="text-center mb-16">
            <Badge variant="outline" className="mb-4 text-sm px-4 py-1">Market Returns</Badge>
            <h2 className="text-3xl md:text-5xl font-bold">What Drove 2025 Returns</h2>
          </AnimatedSection>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {marketHighlights.map((market, index) => (
              <AnimatedSection key={market.region} delay={index * 100} direction="up">
                <Card className="group overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-default h-full">
                  <div className={`h-1.5 bg-gradient-to-r ${market.gradient}`} />
                  <CardContent className="p-8 text-center">
                    <div className={`w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br ${market.gradient} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-500`}>
                      <market.icon className="w-10 h-10 text-white" />
                    </div>
                    <h3 className="font-semibold text-lg mb-3">{market.region}</h3>
                    <div className="text-4xl font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">{market.return}</div>
                    <p className="text-sm text-muted-foreground mt-2">2025 Return</p>
                  </CardContent>
                </Card>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* P Suite Performance Chart */}
      <section className="py-20 md:py-32 bg-background">
        <div className="container mx-auto px-4">
          <AnimatedSection className="text-center mb-16">
            <Badge variant="outline" className="mb-4 text-sm px-4 py-1">Portfolio Performance</Badge>
            <h2 className="text-3xl md:text-5xl font-bold mb-4">P Suite Performance*</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Exceptional returns with consistent benchmark outperformance across all risk levels.
            </p>
            <p className="text-sm text-muted-foreground mt-2">*Performance as of 22/12/2025</p>
          </AnimatedSection>

          <AnimatedSection delay={200}>
            <Card className="max-w-5xl mx-auto shadow-2xl border-0 overflow-hidden">
              <div className="h-2 bg-gradient-to-r from-primary via-primary/80 to-primary/60" />
              <CardContent className="p-6 md:p-10">
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={pSuiteData} barGap={12}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                      <XAxis 
                        dataKey="name" 
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 14 }}
                      />
                      <YAxis 
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: 'hsl(var(--muted-foreground))' }}
                        tickFormatter={(value) => `${value}%`}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '12px',
                          boxShadow: '0 10px 40px -10px rgba(0,0,0,0.2)'
                        }}
                        formatter={(value: number) => [`${value.toFixed(2)}%`, '']}
                      />
                      <Legend wrapperStyle={{ paddingTop: '20px' }} />
                      <Bar 
                        dataKey="return" 
                        name="Portfolio Return" 
                        fill="hsl(var(--primary))" 
                        radius={[6, 6, 0, 0]}
                      />
                      <Bar 
                        dataKey="benchmark" 
                        name="CBAM Benchmark" 
                        fill="hsl(var(--muted-foreground))" 
                        radius={[6, 6, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                
                {/* Performance Table */}
                <div className="mt-10 overflow-x-auto rounded-xl border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left py-4 px-6 font-semibold">Portfolio</th>
                        <th className="text-right py-4 px-6 font-semibold">2025 Return</th>
                        <th className="text-right py-4 px-6 font-semibold">vs Benchmark</th>
                        <th className="text-right py-4 px-6 font-semibold">vs AFI</th>
                        <th className="text-right py-4 px-6 font-semibold">vs ARC MPS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { name: "P1", return: "9.63%", vsBench: "+2.07%", vsAFI: "-", vsARC: "+2.72%" },
                        { name: "P2", return: "11.64%", vsBench: "+2.30%", vsAFI: "+2.34%", vsARC: "+3.48%" },
                        { name: "P3", return: "13.40%", vsBench: "+2.38%", vsAFI: "+2.87%", vsARC: "+3.91%" },
                        { name: "P4", return: "15.54%", vsBench: "+2.77%", vsAFI: "+3.92%", vsARC: "+5.01%" },
                        { name: "P5", return: "17.01%", vsBench: "+2.77%", vsAFI: "+4.31%", vsARC: "+5.36%" },
                      ].map((row, i) => (
                        <tr key={row.name} className={`${i % 2 === 0 ? 'bg-background' : 'bg-muted/30'} hover:bg-primary/5 transition-colors`}>
                          <td className="py-4 px-6 font-bold">{row.name}</td>
                          <td className="py-4 px-6 text-right font-bold text-primary text-lg">{row.return}</td>
                          <td className="py-4 px-6 text-right text-emerald-600 font-medium">{row.vsBench}</td>
                          <td className="py-4 px-6 text-right text-emerald-600 font-medium">{row.vsAFI}</td>
                          <td className="py-4 px-6 text-right text-emerald-600 font-medium">{row.vsARC}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </AnimatedSection>
        </div>
      </section>

      {/* R Suite Performance Chart */}
      <section className="py-20 md:py-32 bg-muted/30">
        <div className="container mx-auto px-4">
          <AnimatedSection className="text-center mb-16">
            <Badge variant="outline" className="mb-4 text-sm px-4 py-1">Portfolio Performance</Badge>
            <h2 className="text-3xl md:text-5xl font-bold mb-4">R Suite Performance*</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Strong performance with consistent outperformance across all risk profiles.
            </p>
            <p className="text-sm text-muted-foreground mt-2">*Performance as of 22/12/2025</p>
          </AnimatedSection>

          <AnimatedSection delay={200}>
            <Card className="max-w-5xl mx-auto shadow-2xl border-0 overflow-hidden">
              <div className="h-2 bg-gradient-to-r from-emerald-500 via-emerald-400 to-teal-400" />
              <CardContent className="p-6 md:p-10">
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={rSuiteData} barGap={12}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                      <XAxis 
                        dataKey="name" 
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 14 }}
                      />
                      <YAxis 
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: 'hsl(var(--muted-foreground))' }}
                        tickFormatter={(value) => `${value}%`}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '12px',
                          boxShadow: '0 10px 40px -10px rgba(0,0,0,0.2)'
                        }}
                        formatter={(value: number) => [`${value.toFixed(2)}%`, '']}
                      />
                      <Legend wrapperStyle={{ paddingTop: '20px' }} />
                      <Bar 
                        dataKey="return" 
                        name="Portfolio Return" 
                        fill="hsl(152, 69%, 40%)" 
                        radius={[6, 6, 0, 0]}
                      />
                      <Bar 
                        dataKey="benchmark" 
                        name="CBAM Benchmark" 
                        fill="hsl(var(--muted-foreground))" 
                        radius={[6, 6, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                
                {/* Performance Table */}
                <div className="mt-10 overflow-x-auto rounded-xl border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left py-4 px-6 font-semibold">Portfolio</th>
                        <th className="text-right py-4 px-6 font-semibold">2025 Return</th>
                        <th className="text-right py-4 px-6 font-semibold">vs Benchmark</th>
                        <th className="text-right py-4 px-6 font-semibold">vs AFI</th>
                        <th className="text-right py-4 px-6 font-semibold">vs ARC MPS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { name: "R1", return: "7.57%", vsBench: "+0.01%", vsAFI: "-", vsARC: "+0.66%" },
                        { name: "R2", return: "10.06%", vsBench: "+0.72%", vsAFI: "+0.76%", vsARC: "+1.90%" },
                        { name: "R3", return: "11.65%", vsBench: "+0.63%", vsAFI: "+1.12%", vsARC: "+2.16%" },
                        { name: "R4", return: "13.63%", vsBench: "+0.86%", vsAFI: "+2.01%", vsARC: "+3.10%" },
                        { name: "R5", return: "15.22%", vsBench: "+0.98%", vsAFI: "+2.52%", vsARC: "+3.57%" },
                      ].map((row, i) => (
                        <tr key={row.name} className={`${i % 2 === 0 ? 'bg-background' : 'bg-muted/30'} hover:bg-emerald-500/5 transition-colors`}>
                          <td className="py-4 px-6 font-bold">{row.name}</td>
                          <td className="py-4 px-6 text-right font-bold text-emerald-600 text-lg">{row.return}</td>
                          <td className="py-4 px-6 text-right text-emerald-600 font-medium">{row.vsBench}</td>
                          <td className="py-4 px-6 text-right text-emerald-600 font-medium">{row.vsAFI}</td>
                          <td className="py-4 px-6 text-right text-emerald-600 font-medium">{row.vsARC}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </AnimatedSection>
        </div>
      </section>

      {/* Active vs Passive Comparison */}
      <section className="py-20 md:py-32 bg-gradient-to-br from-muted/30 to-muted/10 overflow-hidden">
        <div className="container mx-auto px-4">
          <AnimatedSection className="text-center mb-16">
            <Badge variant="outline" className="mb-4 text-sm px-4 py-1">
              <Target className="w-4 h-4 mr-2" />
              Fund Selection Value
            </Badge>
            <h2 className="text-3xl md:text-5xl font-bold">Active vs Passive</h2>
            <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
              Demonstrating the value our active fund selection brings compared to passive alternatives
            </p>
          </AnimatedSection>

          <div className="grid gap-6 max-w-5xl mx-auto">
            {[
              { active: "Artemis SmartGARP European Equity", activeReturn: 54.70, passive: "HSBC European Index", passiveReturn: 25.58 },
              { active: "Artemis SmartGARP Global Emerging Markets", activeReturn: 28.16, passive: "Fidelity Index Emerging Markets", passiveReturn: 22.39 },
              { active: "Lazard Global Listed Infrastructure", activeReturn: 24.94, passive: "L&G Global Infrastructure Index", passiveReturn: 3.40 },
              { active: "Jupiter Merian Global Equity Absolute Return", activeReturn: 16.62, passive: "SONIA (Cash)", passiveReturn: 4.12 },
              { active: "Man Sterling Corporate Bond", activeReturn: 10.44, passive: "Fidelity Index Sterling Corporate Bond", passiveReturn: 6.32 },
              { active: "Royal London Diversified Asset-Backed Credit", activeReturn: 7.31, passive: "SONIA (Cash)", passiveReturn: 4.12 },
            ].map((comparison, index) => {
              const outperformance = comparison.activeReturn - comparison.passiveReturn;
              return (
                <AnimatedSection key={comparison.active} delay={index * 80} direction="up">
                  <Card className="overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                    <div className="grid md:grid-cols-[1fr,auto,1fr] items-center">
                      {/* Active Fund */}
                      <div className="p-6 bg-gradient-to-br from-primary/5 to-primary/10">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className="bg-primary/10 text-primary border-0 text-xs">Active</Badge>
                        </div>
                        <h3 className="font-semibold text-sm md:text-base mb-2 line-clamp-2">{comparison.active}</h3>
                        <div className="text-2xl md:text-3xl font-bold text-primary">
                          +{comparison.activeReturn.toFixed(2)}%
                        </div>
                      </div>

                      {/* VS / Outperformance */}
                      <div className="px-6 py-4 md:py-6 flex flex-row md:flex-col items-center justify-center gap-3 bg-background border-y md:border-y-0 md:border-x border-border/50">
                        <div className="text-xs text-muted-foreground uppercase tracking-wider">Outperformance</div>
                        <div className={`text-xl md:text-2xl font-bold ${outperformance > 0 ? 'text-green-600' : 'text-red-500'}`}>
                          {outperformance > 0 ? '+' : ''}{outperformance.toFixed(2)}%
                        </div>
                        <ArrowRight className="w-5 h-5 text-muted-foreground hidden md:block" />
                      </div>

                      {/* Passive Fund */}
                      <div className="p-6 bg-muted/30">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="secondary" className="text-xs">Passive</Badge>
                        </div>
                        <h3 className="font-semibold text-sm md:text-base mb-2 line-clamp-2 text-muted-foreground">{comparison.passive}</h3>
                        <div className="text-2xl md:text-3xl font-bold text-muted-foreground">
                          +{comparison.passiveReturn.toFixed(2)}%
                        </div>
                      </div>
                    </div>
                  </Card>
                </AnimatedSection>
              );
            })}
          </div>
          <p className="text-sm text-muted-foreground text-center mt-8">*Performance as of 22/12/2025. Figures are as if held since 31/12/2024.</p>
        </div>
      </section>

      {/* Top Performers - Horizontal scroll */}
      <section className="py-20 md:py-32 bg-background overflow-hidden">
        <div className="container mx-auto px-4">
          <AnimatedSection className="text-center mb-16">
            <Badge variant="outline" className="mb-4 text-sm px-4 py-1">
              <Award className="w-4 h-4 mr-2" />
              Top Performers
            </Badge>
            <h2 className="text-3xl md:text-5xl font-bold">Standout Holdings*</h2>
          </AnimatedSection>
          <p className="text-sm text-muted-foreground text-center -mt-12 mb-8">*Performance as of 22/12/2025. Figures are as if held since 31/12/2024.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {topPerformers.map((performer, index) => (
              <AnimatedSection key={performer.fund} delay={index * 80} direction="up">
                <Card className="group h-full overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-default">
                  <div className={`h-1.5 bg-gradient-to-r ${performer.gradient}`} />
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <Badge variant="secondary" className="text-xs">
                        {performer.category}
                      </Badge>
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${performer.gradient} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-500`}>
                        <TrendingUp className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <h3 className="font-semibold text-lg mb-3 line-clamp-2 min-h-[3.5rem]">{performer.fund}</h3>
                    <div className="text-3xl md:text-4xl font-bold text-primary flex items-center gap-2">
                      {performer.return}
                      <ArrowUpRight className="w-7 h-7 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                    </div>
                  </CardContent>
                </Card>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Looking Ahead */}
      <section className="py-20 md:py-32 bg-gradient-to-br from-primary via-primary/95 to-primary/90 text-white overflow-hidden relative">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-1/2 -right-1/4 w-[800px] h-[800px] bg-white/5 rounded-full blur-3xl" />
          <div className="absolute -bottom-1/2 -left-1/4 w-[600px] h-[600px] bg-black/10 rounded-full blur-3xl" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-5xl mx-auto">
            <AnimatedSection className="text-center mb-16">
              <Badge className="mb-4 bg-white/10 text-white border-white/20 text-sm px-4 py-1">2026 Outlook</Badge>
              <h2 className="text-3xl md:text-5xl font-bold">Looking Ahead</h2>
            </AnimatedSection>

            <div className="grid md:grid-cols-2 gap-12">
              <AnimatedSection delay={100} direction="left">
                <div className="space-y-6">
                  <h3 className="text-2xl font-semibold flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                      <Target className="w-5 h-5" />
                    </div>
                    Key Considerations
                  </h3>
                  <ul className="space-y-5">
                    {[
                      { title: "Emerging Markets", desc: "Valuations remain reasonable with continued structural growth drivers" },
                      { title: "UK Equities", desc: "Still attractively valued relative to global peers" },
                      { title: "Fixed Income", desc: "Starting yields of 4-6% support mid-single-digit return expectations" },
                      { title: "Portfolio Construction", desc: "Diversification and risk management for varied conditions" },
                    ].map((item, i) => (
                      <li key={item.title} className="flex gap-4 group">
                        <div className="w-2 h-2 rounded-full bg-white/40 mt-2.5 group-hover:bg-white transition-colors" />
                        <div>
                          <span className="font-semibold">{item.title}:</span>{" "}
                          <span className="text-white/70">{item.desc}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </AnimatedSection>

              <AnimatedSection delay={200} direction="right">
                <div className="space-y-6">
                  <h3 className="text-2xl font-semibold flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                      <LineChart className="w-5 h-5" />
                    </div>
                    Investment Themes
                  </h3>
                  <ul className="space-y-5">
                    {[
                      "Artificial intelligence and technological transformation",
                      "Energy transition and infrastructure investment",
                      "Emerging market consumption growth",
                      "Currency movements and central bank policy",
                      "Valuation discipline in stretched areas",
                    ].map((theme, i) => (
                      <li key={theme} className="flex gap-4 group">
                        <div className="w-2 h-2 rounded-full bg-white/40 mt-2.5 group-hover:bg-white transition-colors" />
                        <span className="text-white/70 group-hover:text-white/90 transition-colors">{theme}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>

      {/* Our Commitment */}
      <section className="py-20 md:py-32 bg-background">
        <div className="container mx-auto px-4">
          <AnimatedSection className="text-center mb-16">
            <Badge variant="outline" className="mb-4 text-sm px-4 py-1">
              <Shield className="w-4 h-4 mr-2" />
              Our Principles
            </Badge>
            <h2 className="text-3xl md:text-5xl font-bold">Our Commitment to You</h2>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {commitments.map((commitment, index) => (
              <AnimatedSection key={commitment.title} delay={index * 80} direction="scale">
                <Card className="h-full border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg mb-2">{commitment.title}</h3>
                        <p className="text-muted-foreground">{commitment.desc}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Thank You Section */}
      <section className="py-20 md:py-32 bg-muted/30">
        <div className="container mx-auto px-4">
          <AnimatedSection className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-5xl font-bold mb-8">Thank You</h2>
            <p className="text-lg md:text-xl text-muted-foreground mb-6">
              Thank you for the trust and confidence you place in Cape Berkshire Asset Management. Managing your wealth is a responsibility we take seriously, and we remain committed to serving your long-term financial objectives.
            </p>
            <p className="text-lg md:text-xl text-muted-foreground mb-10">
              Wishing you and your family health, happiness, and prosperity in the year ahead.
            </p>
            <div className="space-y-2">
              <p className="font-semibold text-xl">The Investment Team</p>
              <p className="text-primary font-bold text-2xl md:text-3xl">Cape Berkshire Asset Management</p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Disclaimer - minimal, not a full footer */}
      <section className="py-10 bg-muted/50 border-t">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h4 className="font-semibold text-sm mb-3">Important Information</h4>
            <p className="text-xs text-muted-foreground leading-relaxed">
              This document is for informational purposes only and does not constitute investment advice. Past performance is not a reliable indicator of future results. The value of investments can fall as well as rise, and you may not get back the amount originally invested. All performance figures are calculated on a bid-to-bid basis with income reinvested, gross of adviser charges and platform fees. Performance reflects the period to 22 December 2025.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default YearEndReview2025;
